import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Phone, WhatsappLogo } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STAGE_ACCENT = {
  new_inquiry: '#2B6EF0',
  contacted: '#06B6D4',
  qualified: '#8B5CF6',
  demo_scheduled: '#E5A100',
  demo_attended: '#F97316',
  application: '#6366F1',
  fee_pending: '#EAB308',
  enrolled: '#10A882',
  lost: '#EF4444',
  on_hold: '#808080',
};

export default function PipelinePage() {
  const [pipeline, setPipeline] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [stageConfig, setStageConfig] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/settings/pipeline-stages')
      .then(r => setStageConfig(r.data.stages || []))
      .catch(console.error);
  }, []);

  const fetchPipeline = async () => {
    try {
      const params = branchFilter !== 'all' ? { branch_id: branchFilter } : {};
      const [pRes, bRes] = await Promise.all([
        api.get('/api/leads/pipeline', { params }),
        api.get('/api/branches'),
      ]);
      setPipeline(pRes.data.stages || []);
      setBranches(bRes.data.branches || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPipeline(); }, [branchFilter]);

  const handleDragStart = (e, leadId, fromStage) => {
    e.dataTransfer.setData('leadId', leadId);
    e.dataTransfer.setData('fromStage', fromStage);
  };

  const handleDrop = async (e, toStage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    const fromStage = e.dataTransfer.getData('fromStage');
    if (fromStage === toStage) return;
    try {
      await api.put(`/api/leads/${leadId}/stage`, { stage: toStage });
      fetchPipeline();
    } catch (err) { console.error(err); }
  };

  // Build ordered pipeline using stageConfig for labels and ordering
  const orderedPipeline = stageConfig.length > 0
    ? stageConfig.map(sc => {
        const found = pipeline.find(p => p.key === sc.id);
        return found ? { ...found, label: sc.label } : { key: sc.id, label: sc.label, count: 0, leads: [] };
      })
    : pipeline;

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground font-mono">loading pipeline...</div>;

  return (
    <div data-testid="pipeline-page" className="stagger-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-1">Drag cards to change stages</p>
        </div>
        <Select value={branchFilter} onValueChange={setBranchFilter}>
          <SelectTrigger data-testid="pipeline-branch-filter" className="w-48 h-9 text-xs rounded-lg">
            <SelectValue placeholder="All Branches" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Branches</SelectItem>
            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="kanban-scroll flex gap-3 pb-4" data-testid="pipeline-kanban">
        {orderedPipeline.filter(s => !['on_hold'].includes(s.key)).map((stage) => {
          const accent = STAGE_ACCENT[stage.key] || '#808080';
          return (
            <div
              key={stage.key}
              className="flex-shrink-0 w-[264px]"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, stage.key)}
              data-testid={`pipeline-col-${stage.key}`}
            >
              {/* Column Header */}
              <div className="flex items-center gap-2 px-1 mb-2.5">
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: accent }} />
                <span className="text-[11px] font-bold text-foreground uppercase tracking-[0.06em]">{stage.label}</span>
                <span className="text-[10px] font-mono text-muted-foreground ml-auto">{stage.count}</span>
              </div>

              {/* Column Body */}
              <div className="bg-secondary/50 border border-border rounded-xl min-h-[280px] max-h-[calc(100vh-220px)] overflow-y-auto p-2 space-y-2">
                {stage.leads.map((lead) => (
                  <Card
                    key={lead.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, lead.id, stage.key)}
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    data-testid={`lead-card-${lead.id}`}
                    className="p-3 border border-border rounded-lg cursor-pointer hover:border-foreground/15 transition-all bg-card card-lift"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-[12px] font-semibold text-foreground truncate flex-1">{lead.student_name}</p>
                      <span className="text-[10px] font-mono text-muted-foreground ml-2">{lead.lead_score}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate mb-2">{lead.parent_name}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-muted-foreground font-mono">{lead.source}</span>
                      <div className="flex gap-1 text-muted-foreground">
                        <Phone size={11} />
                        <WhatsappLogo size={11} />
                      </div>
                    </div>
                    {lead.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {lead.tags.slice(0, 2).map(t => (
                          <span key={t} className="text-[8px] px-1.5 py-0.5 bg-secondary text-muted-foreground rounded font-mono uppercase tracking-wider">{t}</span>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
                {stage.leads.length === 0 && (
                  <div className="flex items-center justify-center h-16 text-[10px] text-muted-foreground font-mono">empty</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
